<template>
<div class="chapter">
<div class="inner_page">
<h3 class="calibre11">THE DESCENT OF MAN</h3>
<h4 class="calibre12 sigil_not_in_toc" id="pgepubid00004">I
</h4>
<p class="calibre13">
When Professor Linyard came back from his holiday in the Maine woods
the air of rejuvenation he brought with him was due less to the
influences of the climate than to the companionship he had enjoyed on
his travels. To Mrs. Linyard's observant eye he had appeared to set out
alone; but an invisible traveller had in fact accompanied him, and if
his heart beat high it was simply at the pitch of his adventure: for
the Professor had eloped with an idea.
</p>
<p class="calibre13">
No one who has not tried the experiment can divine its exhilaration.
Professor Linyard would not have changed places with any hero of
romance pledged to a flesh-and-blood abduction. The most fascinating
female is apt to be encumbered with luggage and scruples: to take up a
good deal of room in the present and overlap inconveniently into the
future; whereas an idea can accommodate itself to a single molecule of
the brain or expand to the circumference of the horizon. The
Professor's companion had to the utmost this quality of adaptability.
As the express train whirled him away from the somewhat inelastic
circle of Mrs. Linyard's affections, his idea seemed to be sitting
opposite him, and their eyes met every moment or two in a glance of
joyous complicity; yet when a friend of the family presently joined him
and began to talk about college matters, the idea slipped out of sight
in a flash, and the Professor would have had no difficulty in proving
that he was alone.
</p>
<p class="calibre13">
But if, from the outset, he found his idea the most agreeable of
fellow-travellers, it was only in the aromatic solitude of the woods
that he tasted the full savour of his adventure. There, during the long
cool August days, lying full length on the pine-needles and gazing up
into the sky, he would meet the eyes of his companion bending over him
like a nearer heaven. And what eyes they were!—clear yet unfathomable,
bubbling with inexhaustible laughter, yet drawing their freshness and
sparkle from the central depths of thought! To a man who for twenty
years had faced an eye reflecting the obvious with perfect accuracy,
these escapes into the inscrutable had always been peculiarly inviting;
but hitherto the Professor's mental infidelities had been restricted by
an unbroken and relentless domesticity. Now, for the first time since
his marriage, chance had given him six weeks to himself, and he was
coming home with his lungs full of liberty.
</p>
<p class="calibre13">
It must not be inferred that the Professor's domestic relations were
defective: they were in fact so complete that it was almost impossible
to get away from them. It is the happy husbands who are really in
bondage; the little rift within the lute is often a passage to freedom.
Marriage had given the Professor exactly what he had sought in it; a
comfortable lining to life. The impossibility of rising to sentimental
crises had made him scrupulously careful not to shirk the practical
obligations of the bond. He took as it were a sociological view of his
case, and modestly regarded himself as a brick in that foundation on
which the state is supposed to rest. Perhaps if Mrs. Linyard had cared
about entomology, or had taken sides in the war over the transmission
of acquired characteristics, he might have had a less impersonal notion
of marriage; but he was unconscious of any deficiency in their
relation, and if consulted would probably have declared that he didn't
want any woman bothering with his beetles. His real life had always
lain in the universe of thought, in that enchanted region which, to
those who have lingered there, comes to have so much more colour and
substance than the painted curtain hanging before it. The Professor's
particular veil of Maia was a narrow strip of homespun woven in a
monotonous pattern; but he had only to lift it to step into an empire.
</p>
<p class="calibre13">
This unseen universe was thronged with the most seductive shapes: the
Professor moved Sultan-like through a seraglio of ideas. But of all the
lovely apparitions that wove their spells about him, none had ever worn
quite so persuasive an aspect as this latest favourite. For the others
were mostly rather grave companions, serious-minded and elevating
enough to have passed muster in a Ladies' Debating Club; but this new
fancy of the Professor's was simply one embodied laugh. It was, in
other words, the smile of relaxation at the end of a long day's toil:
the flash of irony that the laborious mind projects, irresistibly, over
labour conscientiously performed. The Professor had always been a hard
worker. If he was an indulgent friend to his ideas, he was also a stern
task-master to them. For, in addition to their other duties, they had
to support his family: to pay the butcher and baker, and provide for
Jack's schooling and Millicent's dresses. The Professor's household was
a modest one, yet it tasked his ideas to keep it up to his wife's
standard. Mrs. Linyard was not an exacting wife, and she took enough
pride in her husband's attainments to pay for her honours by turning
Millicent's dresses and darning Jack's socks, and going to the College
receptions year after year in the same black silk with shiny seams. It
consoled her to see an occasional mention of Professor Linyard's
remarkable monograph on the Ethical Reactions of the Infusoria, or an
allusion to his investigations into the Unconscious Cerebration of the
Amoeba.
</p>
<p class="calibre13">
Still there were moments when the healthy indifference of Jack and
Millicent reacted on the maternal sympathies; when Mrs. Linyard would
have made her husband a railway-director, if by this transformation she
might have increased her boy's allowance and given her daughter a new
hat, or a set of furs such as the other girls were wearing. Of such
moments of rebellion the Professor himself was not wholly unconscious.
He could not indeed understand why any one should want a new hat; and
as to an allowance, he had had much less money at college than Jack,
and had yet managed to buy a microscope and collect a few "specimens";
while Jack was free from such expensive tastes! But the Professor did
not let his want of sympathy interfere with the discharge of his
paternal obligations. He worked hard to keep the wants of his family
gratified, and it was precisely in the endeavor to attain this end that
he at length broke down and had to cease from work altogether.
</p>
<p class="calibre13">
To cease from work was not to cease from thought of it; and in the
unwonted pause from effort the Professor found himself taking a general
survey of the field he had travelled. At last it was possible to lift
his nose from the loom, to step a moment in front of the tapestry he
had been weaving. From this first inspection of the pattern so long
wrought over from behind, it was natural to glance a little farther and
seek its reflection in the public eye. It was not indeed of his special
task that he thought in this connection. He was but one of the great
army of weavers at work among the threads of that cosmic woof; and what
he sought was the general impression their labour had produced.
</p>
<p class="calibre13">
When Professor Linyard first plied his microscope, the audience of the
man of science had been composed of a few fellow-students, sympathetic
or hostile as their habits of mind predetermined, but versed in the
jargon of the profession and familiar with the point of departure. In
the intervening quarter of a century, however, this little group had
been swallowed up in a larger public. Every one now read scientific
books and expressed an opinion on them. The ladies and the clergy had
taken them up first; now they had passed to the school-room and the
kindergarten. Daily life was regulated on scientific principles; the
daily papers had their "Scientific Jottings"; nurses passed
examinations in hygienic science, and babies were fed and dandled
according to the new psychology.
</p>
<p class="calibre13">
The very fact that scientific investigation still had, to some minds, a
flavour of heterodoxy, gave it a perennial interest. The mob had broken
down the walls of tradition to batten in the orchard of forbidden
knowledge. The inaccessible goddess whom the Professor had served in
his youth now offered her charms in the market-place. And yet it was
not the same goddess after all, but a pseudo-science masquerading in
the garb of the real divinity. This false goddess had her ritual and
her literature. She had her sacred books, written by false priests and
sold by millions to the faithful. In the most successful of these
works, ancient dogma and modern discovery were depicted in a close
embrace under the lime-lights of a hazy transcendentalism; and the
tableau never failed of its effect. Some of the books designed on this
popular model had lately fallen into the Professor's hands, and they
filled him with mingled rage and hilarity. The rage soon died: he came
to regard this mass of pseudo-literature as protecting the truth from
desecration. But the hilarity remained, and flowed into the form of his
idea. And the idea—the divine, incomparable idea—was simply that he
should avenge his goddess by satirizing her false interpreters. He
would write a skit on the "popular" scientific book; he would so heap
platitude on platitude, fallacy on fallacy, false analogy on false
analogy, so use his superior knowledge to abound in the sense of the
ignorant, that even the gross crowd would join in the laugh against its
augurs. And the laugh should be something more than the distension of
mental muscles; it should be the trumpet-blast bringing down the walls
of ignorance, or at least the little stone striking the giant between
the eyes.
</p>
<h4 class="calibre12 sigil_not_in_toc" id="pgepubid00005">
II
</h4>
<p class="calibre13">
The Professor, on presenting his card, had imagined that it would
command prompt access to the publisher's sanctuary; but the young man
who read his name was not moved to immediate action. It was clear that
Professor Linyard of Hillbridge University was not a specific figure to
the purveyors of popular literature. But the publisher was an old
friend; and when the card had finally drifted to his office on the
languid tide of routine he came forth at once to greet his visitor.
</p>
<p class="calibre13">
The warmth of his welcome convinced the Professor that he had been
right in bringing his manuscript to Ned Harviss. He and Harviss had
been at Hillbridge together, and the future publisher had been one of
the wildest spirits in that band of college outlaws which yearly turns
out so many inoffensive citizens and kind husbands and fathers. The
Professor knew the taming qualities of life. He was aware that many of
his most reckless comrades had been transformed into prudent
capitalists or cowed wage-earners; but he was almost sure that he could
count on Harviss. So rare a sense of irony, so keen a perception of
relative values, could hardly have been blunted even by twenty years'
intercourse with the obvious.
</p>
<p class="calibre13">
The publisher's appearance was a little disconcerting. He looked as if
he had been fattened on popular fiction; and his fat was full of
optimistic creases. The Professor seemed to see him bowing into his
office a long train of spotless heroines laden with the maiden tribute
of the hundredth thousand volume.
</p>
<p class="calibre13">
Nevertheless, his welcome was reassuring. He did not disown his early
enormities, and capped his visitor's tentative allusions by such
flagrant references to the past that the Professor produced his
manuscript without a scruple.
</p>
<p class="calibre13">
"What—you don't mean to say you've been doing something in our line?"
</p>
<p class="calibre13">
The Professor smiled. "You publish scientific books sometimes, don't
you?"
</p>
<p class="calibre13">
The publisher's optimistic creases relaxed a little. "H'm—it all
depends—I'm afraid you're a little <i class="calibre14">too</i> scientific for us. We have a
big sale for scientific breakfast foods, but not for the concentrated
essences. In your case, of course, I should be delighted to stretch a
point; but in your own interest I ought to tell you that perhaps one of
the educational houses would do you better."
</p>
<p class="calibre13">
The Professor leaned back, still smiling luxuriously.
</p>
<p class="calibre13">
"Well, look it over—I rather think you'll take it."
</p>
<p class="calibre13">
"Oh, we'll <i class="calibre14">take</i> it, as I say; but the terms might not—"
</p>
<p class="calibre13">
"No matter about the terms—"
</p>
<p class="calibre13">
The publisher threw his head back with a laugh. "I had no idea that
science was so profitable; we find our popular novelists are the
hardest hands at a bargain."
</p>
<p class="calibre13">
"Science is disinterested," the Professor corrected him. "And I have a
fancy to have you publish this thing."
</p>
<p class="calibre13">
"That's immensely good of you, my dear fellow. Of course your name goes
with a certain public—and I rather like the originality of our
bringing out a work so out of our line. I daresay it may boom us both."
His creases deepened at the thought, and he shone encouragingly on the
Professor's leave-taking.
</p>
<p class="calibre13">
Within a fortnight, a line from Harviss recalled the Professor to town.
He had been looking forward with immense zest to this second meeting;
Harviss's college roar was in his tympanum, and he pictured himself
following up the protracted chuckle which would follow his friend's
progress through the manuscript. He was proud of the adroitness with
which he had kept his secret from Harviss, had maintained to the last
the pretense of a serious work, in order to give the keener edge to his
reader's enjoyment. Not since under-graduate days had the Professor
tasted such a draught of pure fun as his anticipations now poured for
him.
</p>
<p class="calibre13">
This time his card brought instant admission. He was bowed into the
office like a successful novelist, and Harviss grasped him with both
hands.
</p>
<p class="calibre13">
"Well—do you mean to take it?" he asked, with a lingering coquetry.
</p>
<p class="calibre13">
"Take it? Take it, my dear fellow? It's in press already—you'll excuse
my not waiting to consult you? There will be no difficulty about terms,
I assure you, and we had barely time to catch the autumn market. My
dear Linyard, why didn't you <i class="calibre14">tell</i> me?" His voice sank to a
reproachful solemnity, and he pushed forward his own arm-chair.
</p>
<p class="calibre13">
The Professor dropped into it with a chuckle. "And miss the joy of
letting you find out?"
</p>
<p class="calibre13">
"Well—it <i class="calibre14">was</i> a joy." Harviss held out a box of his best cigars. "I
don't know when I've had a bigger sensation. It was so deucedly
unexpected—and, my dear fellow, you've brought it so exactly to the
right shop."
</p>
<p class="calibre13">
"I'm glad to hear you say so," said the Professor modestly.
</p>
<p class="calibre13">
Harviss laughed in rich appreciation. "I don't suppose you had a doubt
of it; but of course I was quite unprepared. And it's so
extraordinarily out of your line—"
</p>
<p class="calibre13">
The Professor took off his glasses and rubbed them with a slow smile.
</p>
<p class="calibre13">
"Would you have thought it so—at college?"
</p>
<p class="calibre13">
Harviss stared. "At college?—Why, you were the most iconoclastic
devil—"
</p>
<p class="calibre13">
There was a perceptible pause. The Professor restored his glasses and
looked at his friend. "Well—?" he said simply.
</p>
<p class="calibre13">
"Well—?" echoed the other, still staring. "Ah—I see; you mean that
that's what explains it. The swing of the pendulum, and so forth. Well,
I admit it's not an uncommon phenomenon. I've conformed myself, for
example; most of our crowd have, I believe; but somehow I hadn't
expected it of you."
</p>
<p class="calibre13">
The close observer might have detected a faint sadness under the
official congratulation of his tone; but the Professor was too amazed
to have an ear for such fine shades.
</p>
<p class="calibre13">
"Expected it of me? Expected what of me?" he gasped. "What in heaven do
you think this thing is?" And he struck his fist on the manuscript
which lay between them.
</p>
<p class="calibre13">
Harviss had recovered his optimistic creases. He rested a benevolent
eye on the document.
</p>
<p class="calibre13">
"Why, your apologia—your confession of faith, I should call it. You
surely must have seen which way you were going? You can't have written
it in your sleep?"
</p>
<p class="calibre13">
"Oh, no, I was wide awake enough," said the Professor faintly.
</p>
<p class="calibre13">
"Well, then, why are you staring at me as if I were <i class="calibre14">not?"</i> Harviss
leaned forward to lay a reassuring hand on his visitor's worn
coat-sleeve. "Don't mistake me, my dear Linyard. Don't fancy there was
the least unkindness in my allusion to your change of front. What is
growth but the shifting of the stand-point? Why should a man be
expected to look at life with the same eyes at twenty and at—our age?
It never occurred to me that you could feel the least delicacy in
admitting that you have come round a little—have fallen into line, so
to speak."
</p>
<p class="calibre13">
But the Professor had sprung up as if to give his lungs more room to
expand; and from them there issued a laugh which shook the editorial
rafters.
</p>
<p class="calibre13">
"Oh, Lord, oh Lord—is it really as good as that?" he gasped.
</p>
<p class="calibre13">
Harviss had glanced instinctively toward the electric bell on his desk;
it was evident that he was prepared for an emergency.
</p>
<p class="calibre13">
"My dear fellow—" he began in a soothing tone.
</p>
<p class="calibre13">
"Oh, let me have my laugh out, do," implored the Professor. "I'll—I'll
quiet down in a minute; you needn't ring for the young man." He dropped
into his chair again, and grasped its arms to steady his shaking. "This
is the best laugh I've had since college," he brought out between his
paroxysms. And then, suddenly, he sat up with a groan. "But if it's as
good as that it's a failure!" he exclaimed.
</p>
<p class="calibre13">
Harviss, stiffening a little, examined the tip of his cigar. "My dear
Linyard," he said at length, "I don't understand a word you're saying."
</p>
<p class="calibre13">
The Professor succumbed to a fresh access, from the vortex of which he
managed to fling out—"But that's the very core of the joke!"
</p>
<p class="calibre13">
Harviss looked at him resignedly. "What is?"
</p>
<p class="calibre13">
"Why, your not seeing—your not understanding—"
</p>
<p class="calibre13">
"Not understanding <i class="calibre14">what?"</i>
</p>
<p class="calibre13">
"Why, what the book is meant to be." His laughter subsided again and he
sat gazing thoughtfully at the publisher. "Unless it means," he wound
up, "that I've over-shot the mark."
</p>
<p class="calibre13">
"If I am the mark, you certainly have," said Harviss, with a glance at
the clock.
</p>
<p class="calibre13">
The Professor caught the glance and interpreted it. "The book is a
skit," he said, rising.
</p>
<p class="calibre13">
The other stared. "A skit? It's not serious, you mean?"
</p>
<p class="calibre13">
"Not to me—but it seems you've taken it so."
</p>
<p class="calibre13">
"You never told me—" began the publisher in a ruffled tone.
</p>
<p class="calibre13">
"No, I never told you," said the Professor.
</p>
<p class="calibre13">
Harviss sat staring at the manuscript between them. "I don't pretend to
be up in such recondite forms of humour," he said, still stiffly. "Of
course you address yourself to a very small class of readers."
</p>
<p class="calibre13">
"Oh, infinitely small," admitted the Professor, extending his hand
toward the manuscript.
</p>
<p class="calibre13">
Harviss appeared to be pursuing his own train of thought. "That is," he
continued, "if you insist on an ironical interpretation."
</p>
<p class="calibre13">
"If I insist on it—what do you mean?"
</p>
<p class="calibre13">
The publisher smiled faintly. "Well—isn't the book susceptible of
another? If <i class="calibre14">I</i> read it without seeing—"
</p>
<p class="calibre13">
"Well?" murmured the other, fascinated.—"why shouldn't the rest of the
world?" declared Harviss boldly. "I represent the Average
Reader—that's my business, that's what I've been training myself to do
for the last twenty years. It's a mission like another—the thing is to
do it thoroughly; not to cheat and compromise. I know fellows who are
publishers in business hours and dilettantes the rest of the time.
Well, they never succeed: convictions are just as necessary in business
as in religion. But that's not the point—I was going to say that if
you'll let me handle this book as a genuine thing I'll guarantee to
make it go."
</p>
<p class="calibre13">
The Professor stood motionless, his hand still on the manuscript.
</p>
<p class="calibre13">
"A genuine thing?" he echoed.
</p>
<p class="calibre13">
"A serious piece of work—the expression of your convictions. I tell
you there's nothing the public likes as much as convictions—they'll
always follow a man who believes in his own ideas. And this book is
just on the line of popular interest. You've got hold of a big thing.
It's full of hope and enthusiasm: it's written in the religious key.
There are passages in it that would do splendidly in a Birthday
Book—things that popular preachers would quote in their sermons. If
you'd wanted to catch a big public you couldn't have gone about it in a
better way. The thing's perfect for my purpose—I wouldn't let you
alter a word of it. It'll sell like a popular novel if you'll let me
handle it in the right way."
</p>
<h4 class="calibre12 sigil_not_in_toc" id="pgepubid00006">
III
</h4>
<p class="calibre13">
When the Professor left Harviss's office, the manuscript remained
behind. He thought he had been taken by the huge irony of the
situation—by the enlarged circumference of the joke. In its original
form, as Harviss had said, the book would have addressed itself to a
very limited circle: now it would include the world. The elect would
understand; the crowd would not; and his work would thus serve a double
purpose. And, after all, nothing was changed in the situation; not a
word of the book was to be altered. The change was merely in the
publisher's point of view, and in the "tip" he was to give the
reviewers. The Professor had only to hold his tongue and look serious.
</p>
<p class="calibre13">
These arguments found a strong reinforcement in the large premium which
expressed Harviss's sense of his opportunity. As a satire, the book
would have brought its author nothing; in fact, its cost would have
come out of his own pocket, since, as Harviss assured him, no publisher
would have risked taking it. But as a profession of faith, as the
recantation of an eminent biologist, whose leanings had hitherto been
supposed to be toward a cold determinism, it would bring in a steady
income to author and publisher. The offer found the Professor in a
moment of financial perplexity. His illness, his unwonted holiday, the
necessity of postponing a course of well-paid lectures, had combined to
diminish his resources; and when Harviss offered him an advance of a
thousand dollars the esoteric savour of the joke became irresistible.
It was still as a joke that he persisted in regarding the transaction;
and though he had pledged himself not to betray the real intent of the
book, he held <i class="calibre14">in petto</i> the notion of some day being able to take the
public into his confidence. As for the initiated, they would know at
once: and however long a face he pulled, his colleagues would see the
tongue in his cheek. Meanwhile it fortunately happened that, even if
the book should achieve the kind of triumph prophesied by Harviss, it
would not appreciably injure its author's professional standing.
Professor Linyard was known chiefly as a microscopist. On the structure
and habits of a certain class of coleoptera he was the most
distinguished living authority; but none save his intimate friends knew
what generalizations on the destiny of man he had drawn from these
special studies. He might have published a treatise on the Filioque
without disturbing the confidence of those on whose approval his
reputation rested; and moreover he was sustained by the thought that
one glance at his book would let them into its secret. In fact, so sure
was he of this that he wondered the astute Harviss had cared to risk
such speedy exposure. But Harviss had probably reflected that even in
this reverberating age the opinions of the laboratory do not easily
reach the street; and the Professor, at any rate, was not bound to
offer advice on this point.
</p>
<p class="calibre13">
The determining cause of his consent was the fact that the book was
already in press. The Professor knew little about the workings of the
press, but the phrase gave him a sense of finality, of having been
caught himself in the toils of that mysterious engine. If he had had
time to think the matter over, his scruples might have dragged him
back; but his conscience was eased by the futility of resistance.
</p>
<h4 class="calibre12 sigil_not_in_toc" id="pgepubid00007">
IV
</h4>
<p class="calibre13">
Mrs. Linyard did not often read the papers; and there was therefore a
special significance in her approaching her husband one evening after
dinner with a copy of the <i class="calibre14">New York Investigator</i> in her hand. Her
expression lent solemnity to the act: Mrs. Linyard had a limited but
distinctive set of expressions, and she now looked as she did when the
President of the University came to dine.
</p>
<p class="calibre13">
"You didn't tell me of this, Samuel," she said in a slightly tremulous
voice.
</p>
<p class="calibre13">
"Tell you of what?" returned the Professor, reddening to the margin of
his baldness.
</p>
<p class="calibre13">
"That you had published a book—I might never have heard of it if Mrs.
Pease hadn't brought me the paper."
</p>
<p class="calibre13">
Her husband rubbed his eye-glasses with a groan. "Oh, you would have
heard of it," he said gloomily.
</p>
<p class="calibre13">
Mrs. Linyard stared. "Did you wish to keep it from me, Samuel?" And as
he made no answer, she added with irresistible pride: "Perhaps you
don't know what beautiful things have been said about it."
</p>
<p class="calibre13">
He took the paper with a reluctant hand. "Has Pease been saying
beautiful things about it?"
</p>
<p class="calibre13">
"The Professor? Mrs. Pease didn't say he had mentioned it."
</p>
<p class="calibre13">
The author heaved a sigh of relief. His book, as Harviss had
prophesied, had caught the autumn market: had caught and captured it.
The publisher had conducted the campaign like an experienced
strategist. He had completely surrounded the enemy. Every newspaper,
every periodical, held in ambush an advertisement of "The Vital Thing."
Weeks in advance the great commander had begun to form his lines of
attack. Allusions to the remarkable significance of the coming work had
appeared first in the scientific and literary reviews, spreading thence
to the supplements of the daily journals. Not a moment passed without a
quickening touch to the public consciousness: seventy millions of
people were forced to remember at least once a day that Professor
Linyard's book was on the verge of appearing. Slips emblazoned with the
question: <i class="calibre14">Have you read "The Vital Thing"?</i> fell from the pages of
popular novels and whitened the floors of crowded street-cars. The
query, in large lettering, assaulted the traveller at the railway
bookstall, confronted him on the walls of "elevated" stations, and
seemed, in its ascending scale, about to supplant the interrogations as
to soap and stove-polish which animate our rural scenery.
</p>
<p class="calibre13">
On the day of publication, the Professor had withdrawn to his
laboratory. The shriek of the advertisements was in his ears, and his
one desire was to avoid all knowledge of the event they heralded. A
reaction of self-consciousness had set in, and if Harviss's cheque had
sufficed to buy up the first edition of "The Vital Thing" the Professor
would gladly have devoted it to that purpose. But the sense of
inevitableness gradually subdued him, and he received his wife's copy
of the <i class="calibre14">Investigator</i> with a kind of impersonal curiosity. The review
was a long one, full of extracts: he saw, as he glanced over them, how
well they would look in a volume of "Selections." The reviewer began by
thanking his author "for sounding with no uncertain voice that note of
ringing optimism, of faith in man's destiny and the supremacy of good,
which has too long been silenced by the whining chorus of a decadent
nihilism.... It is well," the writer continued, "when such reminders
come to us not from the moralist but from the man of science—when from
the desiccating atmosphere of the laboratory there rises this glorious
cry of faith and reconstruction."
</p>
<p class="calibre13">
The review was minute and exhaustive. Thanks no doubt to Harviss's
diplomacy, it had been given to the <i class="calibre14">Investigator's</i> "best man," and
the Professor was startled by the bold eye with which his emancipated
fallacies confronted him. Under the reviewer's handling they made up
admirably as truths, and their author began to understand Harviss's
regret that they should be used for any less profitable purpose.
</p>
<p class="calibre13">
The <i class="calibre14">Investigator</i>, as Harviss phrased it, "set the pace," and the
other journals followed, finding it easier to let their critical
man-of-all-work play a variation on the first reviewer's theme than to
secure an expert to "do" the book afresh. But it was evident that the
Professor had captured his public, for all the resources of the
profession could not, as Harviss gleefully pointed out, have carried
the book so straight to the heart of the nation. There was something
noble in the way in which Harviss belittled his own share in the
achievement, and insisted on the inutility of shoving a book which had
started with such headway on.
</p>
<p class="calibre13">
"All I ask you is to admit that I saw what would happen," he said with
a touch of professional pride. "I knew you'd struck the right note—I
knew they'd be quoting you from Maine to San Francisco. Good as
fiction? It's better—it'll keep going longer."
</p>
<p class="calibre13">
"Will it?" said the Professor with a slight shudder. He was resigned to
an ephemeral triumph, but the thought of the book's persistency
frightened him.
</p>
<p class="calibre13">
"I should say so! Why, you fit in everywhere—science, theology,
natural history—and then the all-for-the-best element which is so
popular just now. Why, you come right in with the How-to-Relax series,
and they sell way up in the millions. And then the book's so full of
tenderness—there are such lovely things in it about flowers and
children. I didn't know an old Dryasdust like you could have such a lot
of sentiment in him. Why, I actually caught myself snivelling over that
passage about the snowdrops piercing the frozen earth; and my wife was
saying the other day that, since she's read 'The Vital Thing,' she
begins to think you must write the 'What-Cheer Column,' in the
<i class="calibre14">Inglenook."</i> He threw back his head with a laugh which ended in the
inspired cry: "And, by George, sir, when the thing begins to slow off
we'll start somebody writing against it, and that will run us straight
into another hundred thousand."
</p>
<p class="calibre13">
And as earnest of this belief he drew the Professor a supplementary
cheque.
</p>
<h4 class="calibre12 sigil_not_in_toc" id="pgepubid00008">
V
</h4>
<p class="calibre13">
Mrs. Linyard's knock cut short the importunities of the lady who had
been trying to persuade the Professor to be taken by flashlight at his
study table for the Christmas number of the <i class="calibre14">Inglenook</i>. On this point
the Professor had fancied himself impregnable; but the unwonted smile
with which he welcomed his wife's intrusion showed that his defences
were weakening.
</p>
<p class="calibre13">
The lady from the <i class="calibre14">Inglenook</i> took the hint with professional
promptness, but said brightly, as she snapped the elastic around her
note-book: "I shan't let you forget me, Professor."
</p>
<p class="calibre13">
The groan with which he followed her retreat was interrupted by his
wife's question: "Do they pay you for these interviews, Samuel?"
</p>
<p class="calibre13">
The Professor looked at her with sudden attention. "Not directly," he
said, wondering at her expression.
</p>
<p class="calibre13">
She sank down with a sigh. "Indirectly, then?"
</p>
<p class="calibre13">
"What is the matter, my dear? I gave you Harviss's second cheque the
other day—"
</p>
<p class="calibre13">
Her tears arrested him. "Don't be hard on the boy, Samuel! I really
believe your success has turned his head."
</p>
<p class="calibre13">
"The boy—what boy? My success—? Explain yourself, Susan!"
</p>
<p class="calibre13">
"It's only that Jack has—has borrowed some money—which he can't
repay. But you mustn't think him altogether to blame, Samuel. Since the
success of your book he has been asked about so much—it's given the
children quite a different position. Millicent says that wherever they
go the first question asked is, 'Are you any relation of the author of
"The Vital Thing"?' Of course we're all very proud of the book; but it
entails obligations which you may not have thought of in writing it."
</p>
<p class="calibre13">
The Professor sat gazing at the letters and newspaper clippings on the
study-table which he had just successfully defended from the camera of
the <i class="calibre14">Inglenook</i>. He took up an envelope bearing the name of a popular
weekly paper.
</p>
<p class="calibre13">
"I don't know that the <i class="calibre14">Inglenook</i> would help much," he said, "but I
suppose this might."
</p>
<p class="calibre13">
Mrs. Linyard's eyes glowed with maternal avidity.
</p>
<p class="calibre13">
"What is it, Samuel?"
</p>
<p class="calibre13">
"A series of 'Scientific Sermons' for the Round-the-Gas-Log column of
<i class="calibre14">The Woman's World</i>. I believe that journal has a larger circulation
than any other weekly, and they pay in proportion."
</p>
<p class="calibre13">
He had not even asked the extent of Jack's indebtedness. It had been so
easy to relieve recent domestic difficulties by the timely production
of Harviss's two cheques, that it now seemed natural to get Mrs.
Linyard out of the room by promising further reinforcements. The
Professor had indignantly rejected Harviss's suggestion that he should
follow up his success by a second volume on the same lines. He had
sworn not to lend more than a passive support to the fraud of "The
Vital Thing"; but the temptation to free himself from Mrs. Linyard
prevailed over his last scruples, and within an hour he was at work on
the Scientific Sermons.
</p>
<p class="calibre13">
The Professor was not an unkind man. He really enjoyed making his
family happy; and it was his own business if his reward for so doing
was that it kept them out of his way. But the success of "The Vital
Thing" gave him more than this negative satisfaction. It enlarged his
own existence and opened new doors into other lives. The Professor,
during fifty virtuous years, had been cognizant of only two types of
women: the fond and foolish, whom one married, and the earnest and
intellectual, whom one did not. Of the two, he infinitely preferred the
former, even for conversational purposes. But as a social instrument
woman was unknown to him; and it was not till he was drawn into the
world on the tide of his literary success that he discovered the
deficiencies in his classification of the sex. Then he learned with
astonishment of the existence of a third type: the woman who is fond
without foolishness and intellectual without earnestness. Not that the
Professor inspired, or sought to inspire, sentimental emotions; but he
expanded in the warm atmosphere of personal interest which some of his
new acquaintances contrived to create about him. It was delightful to
talk of serious things in a setting of frivolity, and to be personal
without being domestic.
</p>
<p class="calibre13">
Even in this new world, where all subjects were touched on lightly, and
emphasis was the only indelicacy, the Professor found himself
constrained to endure an occasional reference to his book. It was
unpleasant at first; but gradually he slipped into the habit of hearing
it talked of, and grew accustomed to telling pretty women just how "it
had first come to him."
</p>
<p class="calibre13">
Meanwhile the success of the Scientific Sermons was facilitating his
family relations. His photograph in the <i class="calibre14">Inglenook</i>, to which the lady
of the note-book had succeeded in appending a vivid interview, carried
his fame to circles inaccessible even to "The Vital Thing"; and the
Professor found himself the man of the hour. He soon grew used to the
functions of the office, and gave out hundred-dollar interviews on
every subject, from labour-strikes to Babism, with a frequency which
reacted agreeably on the domestic exchequer. Presently his head began
to figure in the advertising pages of the magazines. Admiring readers
learned the name of the only breakfast-food in use at his table, of the
ink with which "The Vital Thing" had been written, the soap with which
the author's hands were washed, and the tissue-builder which fortified
him for further effort. These confidences endeared the Professor to
millions of readers, and his head passed in due course from the
magazine and the newspaper to the biscuit-tin and the chocolate-box.
</p>
<h4 class="calibre12 sigil_not_in_toc" id="pgepubid00009">
VI
</h4>
<p class="calibre13">
The Professor, all the while, was leading a double life. While the
author of "The Vital Thing" reaped the fruits of popular approval, the
distinguished microscopist continued his laboratory work unheeded save
by the few who were engaged in the same line of investigations. His
divided allegiance had not hitherto affected the quality of his work:
it seemed to him that he returned to the laboratory with greater zest
after an afternoon in a drawing-room where readings from "The Vital
Thing" had alternated with plantation melodies and tea. He had long
ceased to concern himself with what his colleagues thought of his
literary career. Of the few whom he frequented, none had referred to
"The Vital Thing"; and he knew enough of their lives to guess that
their silence might as fairly be attributed to indifference as to
disapproval. They were intensely interested in the Professor's views on
beetles, but they really cared very little what he thought of the
Almighty.
</p>
<p class="calibre13">
The Professor entirely shared their feelings, and one of his chief
reasons for cultivating the success which accident had bestowed on him,
was that it enabled him to command a greater range of appliances for
his real work. He had known what it was to lack books and instruments;
and "The Vital Thing" was the magic wand which summoned them to his
aid. For some time he had been feeling his way along the edge of a
discovery: balancing himself with professional skill on a plank of
hypothesis flung across an abyss of uncertainty. The conjecture was the
result of years of patient gathering of facts: its corroboration would
take months more of comparison and classification. But at the end of
the vista victory loomed. The Professor felt within himself that
assurance of ultimate justification which, to the man of science, makes
a life-time seem the mere comma between premiss and deduction. But he
had reached the point where his conjectures required formulation. It
was only by giving them expression, by exposing them to the comment and
criticism of his associates, that he could test their final value; and
this inner assurance was confirmed by the only friend whose confidence
he invited.
</p>
<p class="calibre13">
Professor Pease, the husband of the lady who had opened Mrs. Linyard's
eyes to the triumph of "The Vital Thing," was the repository of her
husband's scientific experiences. What he thought of "The Vital Thing"
had never been divulged; and he was capable of such vast exclusions
that it was quite possible that pervasive work had not yet reached him.
In any case, it was not likely to affect his judgment of the author's
professional capacity.
</p>
<p class="calibre13">
"You want to put that all in a book, Linyard," was Professor Pease's
summing-up. "I'm sure you've got hold of something big; but to see it
clearly yourself you ought to outline it for others. Take my
advice—chuck everything else and get to work tomorrow. It's time you
wrote a book, anyhow."
</p>
<p class="calibre13">
<i class="calibre14"> It's time you wrote a book, anyhow!</i> The words smote the Professor
with mingled pain and ecstasy: he could have wept over their
significance. But his friend's other phrase reminded him with a start
of Harviss. "You have got hold of a big thing—" it had been the
publisher's first comment on "The Vital Thing." But what a world of
meaning lay between the two phrases! It was the world in which the
powers who fought for the Professor were destined to wage their final
battle; and for the moment he had no doubt of the outcome. The next day
he went to town to see Harviss. He wanted to ask for an advance on the
new popular edition of "The Vital Thing." He had determined to drop a
course of supplementary lectures at the University, and to give himself
up for a year to his book. To do this, additional funds were necessary;
but thanks to "The Vital Thing" they would be forthcoming.
</p>
<p class="calibre13">
The publisher received him as cordially as usual; but the response to
his demand was not as prompt as his previous experience had entitled
him to expect.
</p>
<p class="calibre13">
"Of course we'll be glad to do what we can for you, Linyard; but the
fact is, we've decided to give up the idea of the new edition for the
present."
</p>
<p class="calibre13">
"You've given up the new edition?"
</p>
<p class="calibre13">
"Why, yes—we've done pretty well by 'The Vital Thing,' and we're
inclined to think it's <i class="calibre14">your</i> turn to do something for it now."
</p>
<p class="calibre13">
The Professor looked at him blankly. "What can I do for it?" he
asked—"what <i class="calibre14">more</i>" his accent added.
</p>
<p class="calibre13">
"Why, put a little new life in it by writing something else. The secret
of perpetual motion hasn't yet been discovered, you know, and it's one
of the laws of literature that books which start with a rush are apt to
slow down sooner than the crawlers. We've kept 'The Vital Thing' going
for eighteen months—but, hang it, it ain't so vital any more. We
simply couldn't see our way to a new edition. Oh, I don't say it's dead
yet—but it's moribund, and you're the only man who can resuscitate it."
</p>
<p class="calibre13">
The Professor continued to stare. "I—what can I do about it?" he
stammered.
</p>
<p class="calibre13">
"Do? Why write another like it—go it one better: you know the trick.
The public isn't tired of you by any means; but you want to make
yourself heard again before anybody else cuts in. Write another
book—write two, and we'll sell them in sets in a box: The Vital Thing
Series. That will take tremendously in the holidays. Try and let us
have a new volume by October—I'll be glad to give you a big advance if
you'll sign a contract on that."
</p>
<p class="calibre13">
The Professor sat silent: there was too cruel an irony in the
coincidence.
</p>
<p class="calibre13">
Harviss looked up at him in surprise.
</p>
<p class="calibre13">
"Well, what's the matter with taking my advice—you're not going out of
literature, are you?"
</p>
<p class="calibre13">
The Professor rose from his chair. "No—I'm going into it," he said
simply.
</p>
<p class="calibre13">
"Going into it?"
</p>
<p class="calibre13">
"I'm going to write a real book—a serious one."
</p>
<p class="calibre13">
"Good Lord! Most people think 'The Vital Thing' 's serious."
</p>
<p class="calibre13">
"Yes—but I mean something different."
</p>
<p class="calibre13">
"In your old line—beetles and so forth?"
</p>
<p class="calibre13">
"Yes," said the Professor solemnly.
</p>
<p class="calibre13">
Harviss looked at him with equal gravity. "Well, I'm sorry for that,"
he said, "because it takes you out of our bailiwick. But I suppose
you've made enough money out of 'The Vital Thing' to permit yourself a
little harmless amusement. When you want more cash come back to
us—only don't put it off too long, or some other fellow will have
stepped into your shoes. Popularity don't keep, you know; and the
hotter the success the quicker the commodity perishes."
</p>
<p class="calibre13">
He leaned back, cheerful and sententious, delivering his axioms with
conscious kindliness.
</p>
<p class="calibre13">
The Professor, who had risen and moved to the door, turned back with a
wavering step.
</p>
<p class="calibre13">
"When did you say another volume would have to be ready?" he faltered.
</p>
<p class="calibre13">
"I said October—but call it a month later. You don't need any pushing
nowadays."
</p>
<p class="calibre13">
"And—you'd have no objection to letting me have a little advance now?
I need some new instruments for my real work."
</p>
<p class="calibre13">
Harviss extended a cordial hand. "My dear fellow, that's talking—I'll
write the cheque while you wait; and I daresay we can start up the
cheap edition of 'The Vital Thing' at the same time, if you'll pledge
yourself to give us the book by November.—How much?" he asked, poised
above his cheque-book.
</p>
<p class="calibre13">
In the street, the Professor stood staring about him, uncertain and a
little dazed.
</p>
<p class="calibre13">
"After all, it's only putting it off for six months," he said to
himself; "and I can do better work when I get my new instruments."
</p>
<p class="calibre13">
He smiled and raised his hat to the passing victoria of a lady in whose
copy of "The Vital Thing" he had recently written:
</p>
<p class="calibre13">
<i class="calibre14">Labor est etiam ipsa voluptas.</i>
</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0003.vue?0bb7648d